/**
 * AI CONTEXT
 * Purpose: one standout moment on the Standout Review stream — student
 *   (clickable to the profile) / teacher / class / level / plan / project
 *   context, category-colored left border + tag pills, and Edit / Generate.
 *   Sharing is the share switch
 *   (green when shared) on review and stream cards, and the row checkbox
 *   on the session feed. While shared, the text, the sentence choice,
 *   Generate, and Don't share stay disabled until the switch is turned
 *   off. Approved and sent lock the same way. Photos are not locked. The
 *   like and needs-work thumbs are not on this card.
 * Writes: the share switch, the parent-sentence choice, and edits use
 *   `updateDocumentOrThrow`.
 *   Generate asks the server for a parent-shareable sentence, then saves
 *   `generatedNote` the same way, so a save failure is not reported as a
 *   generate failure.
 * Permissions: `RESOURCES.STANDOUT_REVIEW_PAGE` UPDATE (LEAD_TEACHER+) gates
 *   editing others' moments, sharing, and choosing which sentence parents
 *   see. The moment's own creator can edit and generate their own moment
 *   while it is unshared. A shared one tells them to ask a manager, since
 *   only UPDATE users get the switch.
 * Used by: StandoutReviewStream.
 */
import { CustomerServiceOutlined, EditOutlined } from "@ant-design/icons"
import { App, Button, Skeleton, Space, Tag, Typography } from "antd"
import dayjs from "dayjs"
import { type CSSProperties, useCallback, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import { GptHandler } from "#classes/GptHandler"
import ClickableStudentName from "#components/ClickableStudentName"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  type StandoutMediaEntry,
  type StandoutMomentConstructor,
} from "#customTypes/composedTypes/standoutTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { standoutAuthorName } from "#forms/AddStudent/studentProfileTabHelpers"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { useStandoutMediaUrl } from "#hooks/newHooks/useStandoutMediaUrl"
import { STANDARD_DATE_TIME_FORMAT } from "#utils/dateHelpers"
import {
  deriveFamilyState,
  type FamilyNoteSource,
  isSharedForFamilyState,
  markSentNoteEdited,
  markSentNoteEditedForGenerate,
  markSentNoteEditedForNote,
  NO_CLASS_SESSION_LABEL,
  parentShareText,
  sentNoteSharePatch,
} from "#utils/standoutMomentMigration"

import DontShareReasonModal, {
  DontShareReasonText,
} from "./DontShareReasonModal"
import { useFamilyDelivery } from "./familyDeliveryContext"
import { useFamilyRepeatMoment } from "./familyRepeatContext"
import MomentArtworkStrip from "./MomentArtworkStrip"
import MomentShareSwitch from "./MomentShareSwitch"
import QuickStoryScoreMark from "./QuickStoryScoreMark"
import {
  COMMENT_HISTORY_SIMILAR,
  REVIEW_MUTED,
  REVIEW_SCORE_SLOT,
  reviewEdge,
  reviewEdgeColor,
  ReviewField,
  reviewHalfStyle,
  reviewLineStyle,
  reviewStatusStyle,
  type SentFamilyNote,
  SentFamilyNoteList,
} from "./reviewCardLayout"
import {
  ItemAttribution,
  itemBylineRowStyle,
  ItemKindTitle,
  ItemSentenceStack,
} from "./reviewItemBody"
import { ShareChoice } from "./ShareChoice"
import ShareLockTooltip from "./ShareLockTooltip"
import SimilarSpine, { SIMILAR_SPINE_PX } from "./SimilarSpine"
import StandoutFeedRow, {
  FEED_NOTE_ICON_BG,
  FEED_PENDING_PILL,
  FEED_ROLE_PILL,
  FEED_STORY_ICON_BG,
  FeedShareCheckbox,
  FeedTypeIcon,
} from "./StandoutFeedRow"
import {
  canEditMoment,
  CATEGORY_COLORS,
  feedItemSourceLabel,
  feedStaffRole,
  isMomentShareLocked,
  shareLockTooltip,
} from "./standoutReviewCardHelpers"
import {
  describeMomentLevel,
  familyReviewAutoLabel,
  familyReviewBucket,
  familyStateWrite,
  getMomentLevel,
  isUnviewed,
  type StandoutReviewJoins,
} from "./standoutReviewHelpers"
import {
  itemsSharedMessage,
  momentHasFamilyText,
} from "./standoutSessionGroups"
import { judgeFamilyRepeatAfterSave } from "./useFamilyRepeatJudgment"
import { useQuickStoryScore } from "./useQuickStoryScore"
import { useSingleFlight } from "./useSingleFlight"

const judgeRepeatAfterSave = (studentId: string, momentId: string) => {
  void judgeFamilyRepeatAfterSave(studentId, momentId).catch((err: unknown) => {
    const detail = err instanceof Error ? err.message : String(err)
    console.error("[standout] family repeat judgment after save failed", detail)
  })
}

const { Text, Paragraph } = Typography

const EMPTY_TAG_IDS: readonly Uuid[] = []

interface MomentSelection {
  checked: boolean
  onChange: (checked: boolean) => void
}

interface StandoutMomentCardProps {
  moment: StandoutMomentConstructor
  joins: StandoutReviewJoins
  hideTeacher?: boolean
  /** Present only for a checkable pending row in a student group. */
  selection?: MomentSelection
  /** Student session card: a feed row under the student's name, not its own card. */
  variant?: "card" | "feed" | "item"
  onAudioHeard?: (heardKey: string) => void
  /**
   * False when another message in this class session already shows the
   * scans. The strip itself still picks pieces by classSessionId.
   */
  showArtwork?: boolean
  /** Already-sent family notes for this student. Omitted outside the review. */
  sentNotes?: readonly SentFamilyNote[]
  /**
   * Shared notes on the session card's right column: byline, switch, and
   * the parent sentence as plain text. No editing, Generate, or Don't
   * share while shared; the inline photos stay off because the session
   * card places them. Turning the switch off moves the note back left.
   */
  collapsed?: boolean
  /** The session card places moment photos itself. Audio stays here. */
  hidePhotos?: boolean
  /** Extra context on the date line, such as an earlier note's class. */
  attributionDetail?: string
}

/**
 * Heard vs unheard lives here, not on the moment. Playing the clip flips it.
 */
const MomentPhoto = ({ entry }: { entry: StandoutMediaEntry }) => {
  const theme = useThemeContext()
  const url = useStandoutMediaUrl(entry.storagePath)
  return (
    <div data-testid="sr-moment-photo" data-media-id={entry.id}>
      {url ? (
        <img
          src={url}
          alt={entry.caption || "Class photo"}
          style={{
            maxWidth: 160,
            maxHeight: 120,
            objectFit: "cover",
            display: "block",
            backgroundColor: theme.gray[3],
          }}
        />
      ) : (
        <Text type="secondary">Photo</Text>
      )}
      {entry.caption ? <div>{entry.caption}</div> : null}
    </div>
  )
}

/** The sentence parents get, read-only. Two lines; a click shows the rest. */
const CompactSentence = ({
  momentId,
  text,
}: {
  momentId: string
  text: string
}) => {
  const [expanded, setExpanded] = useState(false)
  if (!text.trim()) return null
  return (
    <button
      type="button"
      data-testid="sr-compact-sentence"
      data-moment-id={momentId}
      data-expanded={expanded ? "true" : "false"}
      aria-expanded={expanded}
      onClick={() => setExpanded((open) => !open)}
      style={{
        padding: 0,
        border: 0,
        background: "transparent",
        textAlign: "left",
        cursor: "pointer",
        font: "inherit",
        fontSize: 12,
        lineHeight: "16px",
        color: "inherit",
        whiteSpace: "normal",
        overflowWrap: "break-word",
        overflow: "hidden",
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: expanded ? "unset" : 2,
      }}
    >
      {text}
    </button>
  )
}

const MomentAudio = ({
  mediaId,
  storagePath,
  onHeard,
}: {
  mediaId: string
  storagePath: string
  onHeard?: () => void
}) => {
  const [heard, setHeard] = useState(false)
  const url = useStandoutMediaUrl(storagePath)

  return (
    <div
      data-testid="sr-moment-audio"
      data-media-id={mediaId}
      data-heard={heard ? "true" : "false"}
      style={{ display: "flex", gap: 8, alignItems: "center" }}
    >
      <audio
        controls
        preload="none"
        src={url ?? undefined}
        onPlay={() => {
          setHeard(true)
          onHeard?.()
        }}
      />
      <Text type="secondary">{heard ? "Heard" : "Unheard"}</Text>
    </div>
  )
}

const MomentMediaList = ({
  media,
  onAudioHeard,
  hidePhotos = false,
}: {
  media: StandoutMediaEntry[]
  onAudioHeard?: (heardKey: string) => void
  hidePhotos?: boolean
}) => {
  const photos = hidePhotos
    ? []
    : media.filter((entry) => entry.kind === "photo")
  const audio = media.filter((entry) => entry.kind === "audio")
  if (photos.length === 0 && audio.length === 0) return null
  return (
    <>
      {photos.map((entry) => (
        <MomentPhoto key={entry.id} entry={entry} />
      ))}
      {audio.map((entry) => (
        <MomentAudio
          key={entry.id}
          mediaId={entry.id}
          storagePath={entry.storagePath}
          onHeard={
            onAudioHeard ? () => onAudioHeard(`audio:${entry.id}`) : undefined
          }
        />
      ))}
    </>
  )
}

const StandoutMomentCard = ({
  moment,
  joins,
  hideTeacher,
  selection,
  variant = "card",
  onAudioHeard,
  showArtwork = true,
  sentNotes,
  collapsed = false,
  hidePhotos = false,
  attributionDetail,
}: StandoutMomentCardProps) => {
  const theme = useThemeContext()
  const { message } = App.useApp()
  const delivery = useFamilyDelivery()
  const { currentUserId } = useUserContext()
  const hasUpdatePermission = useHasPermission(
    RESOURCES.STANDOUT_REVIEW_PAGE,
    USER_ACTIONS.UPDATE
  )

  const { pending: isGenerating, run: runGenerate } = useSingleFlight()
  const { pending: isSharing, run: runShare } = useSingleFlight()
  const { pending: isUpdatingDontShare, run: runDontShare } = useSingleFlight()
  const { pending: isChoosingText, run: runChooseText } = useSingleFlight()
  const [dontShareOpen, setDontShareOpen] = useState(false)

  const repeat = useFamilyRepeatMoment(moment.studentId, moment.id)
  const unviewed = isUnviewed(moment)
  const isEditable = canEditMoment(moment, currentUserId, hasUpdatePermission)

  const student = joins.studentsById[moment.studentId]
  const authorName = standoutAuthorName(
    moment,
    joins.usersById,
    joins.studentsById
  )
  const classObj = moment.classId
    ? joins.classesById[moment.classId]
    : undefined
  const level = getMomentLevel(moment, joins.studentsById, joins.curriculaById)

  const tagIds = moment.tagIds ?? EMPTY_TAG_IDS
  const tagTexts = tagIds.map((tagId) => joins.tagsById[tagId]?.text ?? tagId)
  const categoryColor = moment.category
    ? CATEGORY_COLORS[moment.category]
    : theme.border
  const feed = variant === "feed"
  const staffUser = moment.createdBy
    ? joins.usersById[moment.createdBy]
    : undefined
  const roleLabel = feedStaffRole(!!moment.assistantId?.trim(), staffUser?.role)
  const hasAudio = (moment.media ?? []).some((entry) => entry.kind === "audio")
  const hasPhoto = (moment.media ?? []).some((entry) => entry.kind === "photo")
  const feedKind = hasPhoto && !moment.note?.trim() ? "Photo" : "Quickstory"

  const familyState = deriveFamilyState(moment)
  const sharedWithFamily =
    isSharedForFamilyState(familyState) && moment.category !== "secret"
  // dontShare blocks the send. The row can still be approved underneath,
  // but it must not wear the green shared control while that block is on.
  const shownShared = sharedWithFamily && moment.dontShare !== true
  const shareLocked = isMomentShareLocked(moment, sharedWithFamily)
  const reviewBucket = familyReviewBucket(moment)
  const storyScore = useQuickStoryScore({
    momentId: moment.id,
    note: moment.note,
    category: moment.category,
    bucket: reviewBucket,
    score: moment.quickStoryScore,
  })
  const storyScoreMark =
    storyScore.phase === "hidden" ? null : (
      <QuickStoryScoreMark
        phase={storyScore.phase}
        score={storyScore.score}
        note={moment.note}
        onRetry={storyScore.retry}
      />
    )

  const handleEditSave = useCallback(
    async (patch: { note: string | null }) => {
      const previous = moment.note?.trim() ?? ""
      const next = patch.note?.trim() ?? ""
      const changed = previous !== next
      try {
        await Firebase.getInstance().updateDocumentOrThrow(
          DOC_TYPES.STANDOUT_MOMENTS,
          moment.id,
          {
            note: patch.note,
            ...(changed
              ? markSentNoteEditedForNote(moment, sharedWithFamily)
              : {}),
          }
        )
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err)
        console.error("[standout] edit save failed", detail)
        void message.error("Could not save this note. Try again.")
        throw err
      }
      if (changed) judgeRepeatAfterSave(moment.studentId, moment.id)
      if (changed && sharedWithFamily) {
        delivery.reset(moment.studentId, moment.classSessionId)
      }
    },
    [delivery, message, moment, sharedWithFamily]
  )

  const handleGeneratedSave = useCallback(
    async (patch: { note: string | null }) => {
      const previous = moment.generatedNote?.trim() ?? ""
      const next = patch.note?.trim() ?? ""
      const changed = previous !== next
      try {
        await Firebase.getInstance().updateDocumentOrThrow(
          DOC_TYPES.STANDOUT_MOMENTS,
          moment.id,
          {
            generatedNote: patch.note,
            ...(changed
              ? markSentNoteEditedForGenerate(moment, sharedWithFamily)
              : {}),
          }
        )
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err)
        console.error("[standout] generated note save failed", detail)
        void message.error("Could not save the generated sentence. Try again.")
        throw err
      }
      if (changed) judgeRepeatAfterSave(moment.studentId, moment.id)
      if (changed && sharedWithFamily) {
        delivery.reset(moment.studentId, moment.classSessionId)
      }
    },
    [delivery, message, moment, sharedWithFamily]
  )

  // A secret stays in Jarvis. The family feed and the send clock never
  // include it, so the card does not offer Share or paint it shared.
  const secret = moment.category === "secret"
  const handleShareToggle = useCallback(
    () =>
      runShare(async () => {
        if (!currentUserId || reviewBucket === "undeliverable") return
        if (moment.dontShare === true || secret) return
        const nextShared = !sharedWithFamily
        try {
          await Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.STANDOUT_MOMENTS,
            moment.id,
            {
              ...familyStateWrite(
                nextShared ? "approved" : "pending",
                currentUserId,
                new Date().toISOString(),
                moment.sharedBy
              ),
              ...(nextShared ? sentNoteSharePatch(moment) : {}),
            }
          )
          if (nextShared) {
            delivery.reset(moment.studentId, moment.classSessionId)
          } else {
            delivery.unshared(moment.studentId, moment.classSessionId, {
              kind: "moment",
              id: moment.id,
            })
          }
          void message.success(
            itemsSharedMessage(1, nextShared ? "shared" : "unshared")
          )
        } catch (err) {
          const detail = err instanceof Error ? err.message : String(err)
          console.error("[standout] share toggle failed", detail)
          void message.error(
            nextShared
              ? "Could not share this item. Try again."
              : "Could not unshare this item. Try again."
          )
        }
      }),
    [
      currentUserId,
      delivery,
      message,
      moment,
      reviewBucket,
      runShare,
      secret,
      sharedWithFamily,
    ]
  )

  const handleDontShare = useCallback(
    (dontShare: boolean, reason?: string) =>
      runDontShare(async () => {
        try {
          await Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.STANDOUT_MOMENTS,
            moment.id,
            dontShare
              ? { dontShare, dontShareReason: reason?.trim() ?? "" }
              : { dontShare }
          )
          setDontShareOpen(false)
          if (dontShare) {
            delivery.unshared(moment.studentId, moment.classSessionId, {
              kind: "moment",
              id: moment.id,
            })
          } else if (
            moment.category !== "secret" &&
            deriveFamilyState(moment) === "approved"
          ) {
            delivery.reset(moment.studentId, moment.classSessionId)
          }
          void message.success(
            dontShare
              ? "Message will not be sent."
              : "Message can be shared again."
          )
        } catch (err) {
          const detail = err instanceof Error ? err.message : String(err)
          console.error("[standout] dont-share failed", detail)
          void message.error("Could not update this message. Try again.")
        }
      }),
    [delivery, message, moment, runDontShare]
  )
  const dontShareModal = (
    <DontShareReasonModal
      open={dontShareOpen}
      subject="message"
      pending={isUpdatingDontShare}
      onConfirm={(reason) => void handleDontShare(true, reason)}
      onCancel={() => setDontShareOpen(false)}
    />
  )
  const dontShareReason =
    moment.dontShare === true ? (
      <DontShareReasonText reason={moment.dontShareReason} />
    ) : null

  const handleGenerate = useCallback(
    () =>
      runGenerate(async () => {
        const studentFirstName =
          student?.firstName?.trim() ||
          student?.name?.trim().split(/\s+/)[0] ||
          ""
        if (!studentFirstName) {
          console.error(
            "[standout] generateStandoutNote missing student first name"
          )
          void message.error("This student has no first name.")
          return
        }
        const comment = moment.note?.trim() ?? ""
        if (tagTexts.length === 0 && !comment) {
          void message.error("Add a comment or some keywords first.")
          return
        }
        let result: string | undefined
        try {
          result = await GptHandler.generateStandoutNote({
            studentFirstName,
            category: moment.category ?? "",
            tagTexts,
            note: comment || undefined,
          })
        } catch (err) {
          const detail = err instanceof Error ? err.message : String(err)
          console.error("[standout] generateStandoutNote failed", detail)
          void message.error(
            detail
              ? `Failed to generate note: ${detail}`
              : "Failed to generate note. Try again."
          )
          return
        }
        const note = result?.trim()
        if (!note) {
          console.error(
            "[standout] generateStandoutNote returned an empty note"
          )
          void message.error("No note was generated. Try again.")
          return
        }
        try {
          await Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.STANDOUT_MOMENTS,
            moment.id,
            {
              generatedNote: note,
              ...markSentNoteEditedForGenerate(moment, sharedWithFamily),
            }
          )
          judgeRepeatAfterSave(moment.studentId, moment.id)
          if (sharedWithFamily) {
            delivery.reset(moment.studentId, moment.classSessionId)
          }
        } catch (err) {
          const detail = err instanceof Error ? err.message : String(err)
          console.error("[standout] saving generated note failed", detail)
          void message.error(
            detail
              ? `The note was generated but could not be saved: ${detail}`
              : "The note was generated but could not be saved. Try again."
          )
        }
      }),
    [
      delivery,
      message,
      moment,
      runGenerate,
      sharedWithFamily,
      student,
      tagTexts,
    ]
  )

  const keywordOnly = reviewBucket === "pending" && !momentHasFamilyText(moment)
  // Teacher, day, and level cards never had a row checkbox. Only the session
  // feed did, so a keyword-only note stays a disabled box there and nowhere else.
  const selectionNode =
    keywordOnly && hasUpdatePermission && feed ? (
      <FeedShareCheckbox
        testId="sr-moment-checkbox"
        shared={false}
        checked={false}
        disabled
        ariaLabel="Add a message before sharing"
        dataAttrs={{ "data-moment-id": moment.id }}
        onChange={() => undefined}
      />
    ) : hasUpdatePermission &&
      !secret &&
      reviewBucket !== "undeliverable" &&
      (feed ? shownShared || selection : selection) ? (
      <FeedShareCheckbox
        testId="sr-moment-checkbox"
        shared={feed && shownShared}
        checked={!!selection?.checked}
        disabled={isSharing}
        ariaLabel={feed && shownShared ? "Unshare moment" : "Select moment"}
        dataAttrs={{ "data-moment-id": moment.id }}
        onChange={(checked) => {
          if (feed && shownShared) {
            if (!checked) void handleShareToggle()
            return
          }
          selection?.onChange(checked)
        }}
      />
    ) : null

  const status = (
    <>
      {shownShared && reviewBucket !== "sent" && reviewBucket !== "auto" ? (
        <Tag color="green" data-testid="sr-shared-tag">
          Shared
        </Tag>
      ) : null}
      {reviewBucket === "auto" ? (
        <Tag data-testid="sr-auto-tag">{familyReviewAutoLabel(moment)}</Tag>
      ) : null}
      {reviewBucket === "sent" ? (
        <Tag data-testid="sr-sent-tag">Sent</Tag>
      ) : null}
      {reviewBucket === "undeliverable" ? (
        <Tag data-testid="sr-no-session-tag">{NO_CLASS_SESSION_LABEL}</Tag>
      ) : null}
      {feed &&
      !shownShared &&
      reviewBucket !== "auto" &&
      reviewBucket !== "undeliverable" ? (
        <span style={FEED_PENDING_PILL}>Pending</span>
      ) : null}
    </>
  )

  const teacherNote = moment.note?.trim() ?? ""
  const generatedSentence = moment.generatedNote?.trim() ?? ""
  const chooseShareText = teacherNote.length > 0 && generatedSentence.length > 0
  const shareTextChoice: FamilyNoteSource =
    moment.familyNoteSource === "generatedNote" ? "generatedNote" : "note"
  const handleShareTextChoice = useCallback(
    (next: FamilyNoteSource) =>
      runChooseText(async () => {
        if (!hasUpdatePermission || shareLocked || next === shareTextChoice) {
          return
        }
        try {
          await Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.STANDOUT_MOMENTS,
            moment.id,
            {
              familyNoteSource: next,
              ...markSentNoteEdited(moment, sharedWithFamily),
            }
          )
        } catch (err) {
          const detail = err instanceof Error ? err.message : String(err)
          console.error("[standout] share text choice failed", detail)
          void message.error(
            "Could not change the sentence parents see. Try again."
          )
          return
        }
        if (sharedWithFamily) {
          delivery.reset(moment.studentId, moment.classSessionId)
        }
      }),
    [
      delivery,
      hasUpdatePermission,
      message,
      moment,
      runChooseText,
      shareLocked,
      shareTextChoice,
      sharedWithFamily,
    ]
  )

  const lockTooltip = shareLockTooltip(hasUpdatePermission)
  const generateButton = (label: string, style?: CSSProperties) =>
    isEditable ? (
      <ShareLockTooltip locked={shareLocked} title={lockTooltip}>
        <Button
          data-testid="sr-generate-button"
          size="small"
          loading={isGenerating}
          disabled={shareLocked}
          onClick={() => void handleGenerate()}
          style={{
            width: "fit-content",
            ...style,
            pointerEvents: shareLocked ? "none" : undefined,
          }}
        >
          {label}
        </Button>
      </ShareLockTooltip>
    ) : null
  // The teacher's words and the generated sentence are two choices.
  // Parents see one of them. Eval text stays on the card for staff.
  const sentenceStack = (
    <>
      <ItemSentenceStack
        teacherNote={teacherNote}
        generatedSentence={generatedSentence}
        chooseShareText={chooseShareText}
        shareTextChoice={shareTextChoice}
        canChoose={hasUpdatePermission}
        isEditable={isEditable}
        locked={shareLocked}
        lockedReason={lockTooltip}
        busy={isChoosingText}
        note={moment.note}
        generatedNote={moment.generatedNote}
        onSelect={(next) => {
          void handleShareTextChoice(next)
        }}
        onSaveNote={handleEditSave}
        onSaveGenerated={handleGeneratedSave}
      />
      {isGenerating && !generatedSentence ? (
        <div data-testid="sr-generating" aria-busy="true">
          <Skeleton
            active
            title={false}
            paragraph={{ rows: 1, width: "80%" }}
          />
        </div>
      ) : null}
    </>
  )
  const evalText = moment.generatedText ? (
    <Paragraph
      data-testid="sr-moment-generated"
      type="secondary"
      style={{ margin: 0, whiteSpace: "pre-wrap" }}
    >
      <Text strong type="secondary" style={{ fontSize: 11 }}>
        Generated (eval):{" "}
      </Text>
      {moment.generatedText}
    </Paragraph>
  ) : null
  const sentences = (
    <>
      {sentenceStack}
      {evalText}
    </>
  )
  const copy = (
    <>
      {sentences}
      <MomentMediaList media={moment.media ?? []} onAudioHeard={onAudioHeard} />
    </>
  )

  const actions = (
    <Flex gap={10} wrap style={{ alignItems: "center" }}>
      {hasUpdatePermission && !feed ? (
        moment.dontShare === true ? (
          <Button
            data-testid="sr-dont-share-message"
            data-moment-id={moment.id}
            size="small"
            loading={isUpdatingDontShare}
            onClick={() => void handleDontShare(false)}
            style={{ width: "fit-content" }}
          >
            Allow share
          </Button>
        ) : (
          <Button
            data-testid="sr-dont-share-message"
            data-moment-id={moment.id}
            size="small"
            loading={isUpdatingDontShare}
            onClick={() => setDontShareOpen(true)}
            style={{ width: "fit-content" }}
          >
            Don't share
          </Button>
        )
      ) : null}

      {generateButton("Generate")}
    </Flex>
  )

  const keywordPills =
    tagTexts.length > 0 ? (
      <Space size={4} wrap>
        {tagTexts.map((text, index) => (
          <Tag key={`${tagIds[index]}-${text}`} color={categoryColor}>
            {text}
          </Tag>
        ))}
      </Space>
    ) : null

  const feedMarks = feed ? (
    <Space size={4} wrap>
      {status}
    </Space>
  ) : null

  if (feed) {
    return (
      <StandoutFeedRow
        testId="sr-moment-card"
        shared={shownShared}
        selection={selectionNode}
        rowProps={{
          "data-moment-id": moment.id,
          "data-unviewed": unviewed ? "true" : "false",
          "data-shared": shownShared ? "true" : "false",
          "data-student-id": moment.studentId,
          "data-category": moment.category,
          "data-family-bucket": reviewBucket,
        }}
      >
        <Flex gap={8} wrap style={{ alignItems: "center" }}>
          <FeedTypeIcon
            background={
              hasAudio && !moment.note ? FEED_STORY_ICON_BG : FEED_NOTE_ICON_BG
            }
          >
            {hasAudio && !moment.note ? (
              <CustomerServiceOutlined />
            ) : (
              <EditOutlined />
            )}
          </FeedTypeIcon>
          <Text strong>{authorName}</Text>
          <span style={FEED_ROLE_PILL}>{roleLabel}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(moment.createdAt).format("h:mm A")}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            · {feedKind}
          </Text>
          <span style={FEED_ROLE_PILL}>
            {feedItemSourceLabel(moment.source)}
          </span>
          {storyScoreMark}
        </Flex>
        <AnimateInFlex
          animation={ANIMATION_BY_KEY.leftSm}
          col
          gap={8}
          style={{ width: "100%" }}
        >
          {copy}
          {keywordPills}
        </AnimateInFlex>
        {feedMarks}
        {actions}
      </StandoutFeedRow>
    )
  }

  const blocked = moment.dontShare === true
  const edge = reviewEdge(blocked, shownShared)
  const edgeColor = reviewEdgeColor(edge, {
    green: theme.eric.green,
    red: theme.eric.red,
    gray: REVIEW_MUTED,
  })
  const parentText = parentShareText(moment)
  const classTeacherId = classObj?.defaultTeacherId || classObj?.teacherIds?.[0]
  const classTeacher = classTeacherId
    ? (joins.usersById[classTeacherId]?.name?.trim() ?? "")
    : ""
  const bylineName =
    !hideTeacher || moment.assistantId?.trim() ? authorName : ""
  const earlierNotes = (sentNotes ?? []).filter((note) => note.id !== moment.id)
  const shareSwitch =
    hasUpdatePermission && !secret ? (
      <MomentShareSwitch
        momentId={moment.id}
        shared={shownShared}
        loading={isSharing}
        disabled={reviewBucket === "undeliverable" || blocked}
        similar={repeat.similar}
        onToggle={() => void handleShareToggle()}
      />
    ) : null
  const dontShareControlStyle = {
    flex: "0 0 auto" as const,
    color: blocked ? theme.eric.red : REVIEW_MUTED,
    borderColor: blocked ? theme.eric.red : REVIEW_MUTED,
  }

  if (variant === "item") {
    const itemPadding = collapsed ? 6 : 12
    return (
      <div
        data-testid="sr-moment-card"
        data-moment-id={moment.id}
        data-unviewed={unviewed ? "true" : "false"}
        data-student-id={moment.studentId}
        data-category={moment.category}
        data-family-bucket={reviewBucket}
        data-shared={shownShared ? "true" : "false"}
        data-collapsed={collapsed ? "true" : "false"}
        data-similar={repeat.similar ? "true" : "false"}
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: collapsed ? 4 : 6,
          padding: itemPadding,
          paddingLeft: repeat.similar
            ? itemPadding + SIMILAR_SPINE_PX
            : itemPadding,
          borderRadius: 8,
          border: `1px solid ${edgeColor}`,
          borderLeft: `4px solid ${edgeColor}`,
          backgroundColor: repeat.similar
            ? COMMENT_HISTORY_SIMILAR
            : theme.white,
          minWidth: 0,
        }}
      >
        <SimilarSpine similar={repeat.similar} reason={repeat.reason} />
        <div style={itemBylineRowStyle}>
          <ItemKindTitle>Standout moment</ItemKindTitle>
          {storyScoreMark && !collapsed ? (
            <span
              data-testid="sr-item-story-score"
              style={{ flex: "0 0 auto", alignSelf: "center" }}
            >
              {storyScoreMark}
            </span>
          ) : null}
          {hasUpdatePermission && !secret ? (
            <ShareChoice
              state={blocked ? "blocked" : shownShared ? "shared" : "open"}
              loading={isSharing || isUpdatingDontShare}
              shareDisabled={reviewBucket === "undeliverable"}
              confirmShare={repeat.similar && !shownShared}
              onShare={() => void handleShareToggle()}
              onUnshare={() => void handleShareToggle()}
              onDontShare={() => setDontShareOpen(true)}
              onAllow={() => void handleDontShare(false)}
              momentId={moment.id}
            />
          ) : null}
        </div>
        {collapsed ? (
          <CompactSentence momentId={moment.id} text={parentText} />
        ) : (
          <>
            {dontShareReason}
            {dontShareModal}
            {sentenceStack}
            {generateButton(generatedSentence ? "Regenerate" : "Generate")}
            {evalText}
            {keywordPills}
          </>
        )}
        <MomentMediaList
          media={moment.media ?? []}
          onAudioHeard={onAudioHeard}
          hidePhotos={hidePhotos || collapsed}
        />
        <ItemAttribution
          verb="written by"
          name={bylineName}
          when={[
            dayjs(moment.createdAt).format(STANDARD_DATE_TIME_FORMAT),
            attributionDetail ?? "",
          ]
            .filter((part) => part.length > 0)
            .join(" · ")}
        />
      </div>
    )
  }

  return (
    // A plain div, NOT the Flex block: Flex destructures its props and
    // forwards nothing extra, so data-testid/data-* on it never render
    // (see test-environment skill).
    <div
      data-testid="sr-moment-card"
      data-moment-id={moment.id}
      data-unviewed={unviewed ? "true" : "false"}
      data-student-id={moment.studentId}
      data-category={moment.category}
      data-family-bucket={reviewBucket}
      data-shared={shownShared ? "true" : "false"}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${edgeColor}`,
        borderLeft: `4px solid ${edgeColor}`,
        backgroundColor: theme.gray[1],
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={reviewHalfStyle}>
          <div
            style={{
              ...reviewLineStyle,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                flex: "1 1 auto",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {student ? (
                <ClickableStudentName
                  studentId={student.id}
                  label={student.name?.trim() || moment.studentId}
                />
              ) : (
                moment.studentId
              )}
            </span>
            <span
              style={{
                flex: `0 0 ${REVIEW_SCORE_SLOT}px`,
                width: REVIEW_SCORE_SLOT,
                overflow: "hidden",
              }}
            >
              {storyScoreMark}
            </span>
          </div>
          <ReviewField>{level ? describeMomentLevel(level) : ""}</ReviewField>
          <ReviewField>{classObj?.name ?? ""}</ReviewField>
          <ReviewField>{classTeacher}</ReviewField>
          <div style={reviewStatusStyle}>
            <span
              style={{
                flex: "0 0 auto",
                whiteSpace: "nowrap",
                color: edgeColor,
              }}
            >
              {blocked ? "Don't share" : shownShared ? "Shared" : "Not shared"}
            </span>
            {shareSwitch}
            {hasUpdatePermission && (!shownShared || blocked) ? (
              blocked ? (
                <Button
                  data-testid="sr-dont-share-message"
                  data-moment-id={moment.id}
                  size="small"
                  loading={isUpdatingDontShare}
                  onClick={() => void handleDontShare(false)}
                  style={dontShareControlStyle}
                >
                  Allow share
                </Button>
              ) : (
                <Button
                  data-testid="sr-dont-share-message"
                  data-moment-id={moment.id}
                  size="small"
                  loading={isUpdatingDontShare}
                  disabled={shareLocked}
                  onClick={() => setDontShareOpen(true)}
                  style={dontShareControlStyle}
                >
                  Don't share
                </Button>
              )
            ) : null}
          </div>
          {dontShareReason}
          {dontShareModal}
          {sentNotes ? (
            <SentFamilyNoteList
              notes={earlierNotes}
              cardText={parentText}
              markColor={theme.eric.red}
            />
          ) : null}
        </div>
        <div style={reviewHalfStyle}>
          <ReviewField>{bylineName}</ReviewField>
          <ReviewField>
            {dayjs(moment.createdAt).format(STANDARD_DATE_TIME_FORMAT)}
          </ReviewField>
          {sentences}
          {generateButton("Generate", { marginTop: 8 })}
          {keywordPills}
          <MomentMediaList
            media={moment.media ?? []}
            onAudioHeard={onAudioHeard}
          />
          {showArtwork ? (
            <MomentArtworkStrip
              studentId={moment.studentId}
              student={student}
              moment={moment}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default StandoutMomentCard
